import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Select from "react-select";
import { get, post as lowLevelPost } from "../../Common/api";
import { defaultErrorMessage } from "../../Common/UserException";
import config from "../../config";
import { setLoginRedirect } from "../../redux/loginRedirectSlice";

const LoginForm = () => {
  const [errorMessage, setErrorMessage] = useState();
  const [validationMessage, setValidationMessage] = useState();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [users, setUsers] = useState([]);
  const [clientId, setClientId] = useState();
  const [adminUserCustomerId, setAdminUserCustomerId] = useState();
  const [usernameValidationMessage, setUsernameValidationMessage] = useState();
  const [passwordValidationMessage, setPasswordValidationMessage] = useState();
  const [emailValidationMessage, setEmailValidationMessage] = useState();
  const [confirmEmailValidationMessage, setConfirmEmailValidationMessage] =
    useState();
  const [formHistory, setFormHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("start");
  const { search } = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(search);
  const error = queryParams.get("error");
  const returnUrl = queryParams.get("returnUrl");
  const dispatch = useDispatch();

  useEffect(() => {
    // Logout Admin API
    get(`/Account/Logout`).then(() => {}).catch(e => console.error(e));
    // Logout Admin website
    fetch(`${config.adminPortalWebsiteOrigin}/Account/Logout`).then(() => {}).catch(e => console.error(e));
  }, []);

  useEffect(() => {
    if (error === "access-denied") {
      setErrorMessage("You do not have permission to log in. Please see your system administrator.");
    } else if (error) {
      setErrorMessage("An error occurred and your request could not be processed");
    }
  }, [error])
  
  useEffect(() => {
    if (returnUrl) {
      document.cookie = `returnUrl=${encodeURIComponent(returnUrl)}; SameSite=Strict; Secure`;
    }
  }, [returnUrl]);

  const title =
    step === "login" ? null : step === "email" ? (
      "Your organization is using a new way to sign in. " +
      "Please enter your email address so we can get started."
    ) : step === "vanco" ? (
      <React.Fragment>
        <p>
          We've sent you an email inviting you to sign up.
          Once complete, you may sign in with your new
          email and password.
        </p>
      </React.Fragment>
    ) : step === "existing-vanco" ? (
      <React.Fragment>
        <p>
        Looks like you already have an account associated with that email address.
        Please enter the email address and click Next to continue.
        </p>
      </React.Fragment>
    ) : step === "reset-email-sent" ? (
      "A reset password link has been sent to any accounts registered with your email. " +
      "Please check your email to complete the process."
    ) : step === "select-client" ? (
      "There is more than one organization associated with this user account. Please select an organization to continue."
    ) :null;

  const post = async (url, body) => {
    try {
      return await lowLevelPost(url, body);
    } catch (e) {
      const message = e.message || defaultErrorMessage;
      return { status: "error", message: message, data: e.data };
    }
  };

  const clearAllMessages = () => {
    setErrorMessage();
    setValidationMessage();
    setConfirmEmailValidationMessage();
    setEmailValidationMessage();
    setPasswordValidationMessage();
    setUsernameValidationMessage();
  };

  const handleSubmitInitialForm = event => {
    event.preventDefault();
    submitInitialForm();
  };

  const handleError = (result) => {
    const message = (result && result.message) || defaultErrorMessage;
    setErrorMessage(message);

    if (result && result.data && result.data.type === "validation") {
      // Do not need an overall error message for field validation
      setErrorMessage();
      const errors = result.data.errors;
      for (let source in errors) {
        if (source === "email") {
          setEmailValidationMessage(errors[source]);
        }
      }
    }
  };

  const goToStep = (nextStep) => {
    clearAllMessages();
    const prevStep = step;
    setStep(nextStep);
    const newHistory = [...formHistory, prevStep];
    setFormHistory(newHistory);
  };

  const goBack = () => {
    const index = Math.max(0, formHistory.length - 1);
    clearAllMessages();
    const prevStep = formHistory[index];
    const newHistory = formHistory.slice(0, index);
    setStep(prevStep);
    setFormHistory(newHistory);
  };

  const submitInitialForm = async () => {
    clearAllMessages();
    if (!username) {
        setEmailValidationMessage("Username is required");
    } else {
      setLoading(true);
      const result = await post(`/Account/CheckSsoProvider`, {
        username,
      });
      setLoading(false);
      if (!result || result.status === "error") {
        handleError(result);
      } else if (result.status === "sso") {
        // Login through IDS page since this user is managed by an external SSO provider
        const idsLoginUrl = `${config.adminApiOrigin}/Account/IdentityServiceLogin?username=${encodeURIComponent(username)}`;
        dispatch(setLoginRedirect({
          url: idsLoginUrl,
          message: "Redirecting you to your Single Sign-on Provider"
        }));
        history.push(`${config.loginRoot}/redirect`);
      } else {
        setEmail("");
        setConfirmEmail("");
        goToStep("login");
      }
    }
  };

  const login = async () => {
    clearAllMessages();
    if (!password) {
      setPasswordValidationMessage("Password is required");
    } else {
      setLoading(true);
      const result = await post(`/Account/Login`, {
        username,
        password,
        clientId,
        adminUserCustomerId,
      });
        setLoading(false);
      if (result && result.status === "collect-email") {
        setEmail("");
        setConfirmEmail("");
        goToStep("email");
      } else if (result && result.status === "authenticated") {
        dispatch(setLoginRedirect({
          
          url: `${config.adminPortalWebsiteOrigin}/Account/BeginSession`,
          message: "Please wait while we log you in"
        }));
        history.push(`${config.loginRoot}/redirect`);
      } else if (result && result.status === "multiple-users") {
        setErrorMessage(
          "There is more than one organization associated with that user. Please select an organization to continue."
        );
        setUsers(result.users);
        goToStep("select-client")
      } else {
        handleError(result);
      }
    }
  };

  const onClickSubmit = async () => {
    clearAllMessages();
    if (!email) {
      setEmailValidationMessage("Email is required");
    }
    if (!confirmEmail) {
      setConfirmEmailValidationMessage("Confirm email is required");
    }
    if (email !== confirmEmail) {
      setValidationMessage("Email addresses must match");
    }
    if (email && !email.includes("@")) {
      setEmailValidationMessage(`Email '${email}' is invalid.`);
    }
    if (email && email.includes("@") && confirmEmail && email === confirmEmail) {
      setLoading(true);
      const result = await post(`/Account/Invite`, {
        username,
        password,
        email,
      });
      setLoading(false);
      if (result.status === "invitation-sent") {
        goToStep("vanco");
        setUsername(email);
      } else if (result.status === "collect-password") {
        goToStep("existing-vanco");
        setUsername(email);
      } else {
        handleError(result);
      }
    }
  };

  const errorMessageClass =
    "alert alert-danger " +
    (errorMessage ? "display-show" : "display-hide");

  let usernameClass = "form-group";
  if (usernameValidationMessage) {
    usernameClass += " has-error";
  }

  let passwordClass = "form-group";
  if (passwordValidationMessage) {
    passwordClass += " has-error";
  }

  const clientClass = "form-group";

  let clientOptions = users.map((u) => ({
    label: `${u.clientName} (${u.clientId})`,
    value: `${u.clientId}:${u.adminUserCustomerId}`,
  }));

  const clientSelectStyles = {
    menu: (styles, state) => ({
      ...styles,
      zIndex: 10,
    }),
    placeholder: (styles, state) => ({
      ...styles,
      paddingLeft: "25px",
    }),
    singleValue: (styles, state) => ({
      ...styles,
      paddingLeft: "25px",
    }),
  };

  const backFromSelectClientForm = () => {
    setUsers([]);
    setClientId();
    setAdminUserCustomerId();
    goBack();
  }

  const selectClient = (option) => {
    const [newClientId, newAdminUserCustomerId] = option.value
      .split(":")
      .map((v) => Number(v));
    setClientId(newClientId);
    setAdminUserCustomerId(newAdminUserCustomerId);
  };

  let emailClass = "form-group";
  if (emailValidationMessage) {
    emailClass += " has-error";
  }

  const initialForm = (
    <React.Fragment>
      <form name="login" key="login" onSubmit={handleSubmitInitialForm}>
        <div className={emailClass}>
          {/*ie8, ie9 does not support html5 placeholder, so we just show field title for that*/}
          <label className="control-label visible-ie8 visible-ie9">
            Email or Username
          </label>
          <div className="input-icon">
            <i className="fa fa-user" />
            <input
              id="username"
              className="form-control placeholder-no-fix"
              type="text"
              placeholder="Email or Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              name="username"
            />
          </div>
          {emailValidationMessage ? (
            <span htmlFor="username" className="help-block">
              {emailValidationMessage}
            </span>
          ) : null}
        </div>
        <div className="form-actions">
          <button
            type="button"
            disabled={loading}
            className="btn green pull-right"
            aria-label="next"
            onClick={() => submitInitialForm()}
          >
            Next
            { loading
              ? <i className="fa fa-spinner fa-spin" />
              : <i className="m-icon-swapright m-icon-white" />
            }
          </button>
        </div>{" "}
      </form>
    </React.Fragment>
  ) 

  const backButton = (<button
      type="button"
      className="btn grey pull-left"
      onClick={() => goBack()}
      aria-label="back"
    >
      Back <i className="m-icon-swapleft m-icon-white" />
    </button>);

  const loginForm = (
    <React.Fragment>
      <form name="login" key="login">
        {clientOptions.length > 1 ? (
          <div className={clientClass}>
            {/*ie8, ie9 does not support html5 placeholder, so we just show field title for that*/}
            <label
              htmlFor="selectClient"
              className="control-label visible-ie8 visible-ie9"
            >
              Organization
            </label>
            <div className="input-icon">
              <i className="fa fa-building" />
              <Select
                inputId="selectClient"
                styles={clientSelectStyles}
                options={clientOptions}
                onChange={selectClient}
              />
            </div>{" "}
          </div>
        ) : null}
        <div className={usernameClass}>
          {/*ie8, ie9 does not support html5 placeholder, so we just show field title for that*/}
          <label className="control-label visible-ie8 visible-ie9">
            Email or Username
          </label>
          <div className="input-icon">
            <i className="fa fa-user" />
            <input
              id="username"
              className="form-control placeholder-no-fix"
              type="text"
              placeholder="Email or Username"
              value={username}
              disabled={true}
              name="username"
            />
          </div>
          {usernameValidationMessage ? (
            <span htmlFor="password" className="help-block">
              {usernameValidationMessage}
            </span>
          ) : null}
        </div>
        <div className={passwordClass}>
          <label className="control-label visible-ie8 visible-ie9">
            Password
          </label>
          <div className="input-icon">
            <i className="fa fa-lock" />
            <input
              id="password"
              className="form-control placeholder-no-fix"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              name="password"
            />
          </div>
          {passwordValidationMessage ? (
            <span htmlFor="password" className="help-block">
              {passwordValidationMessage}
            </span>
          ) : null}
        </div>
        <div className="form-actions">
          { backButton }
          <button
            type="button"
            className="btn green pull-right"
            aria-label="login"
            disabled={loading}
            onClick={() => login()}
          >
            Log in
            { loading
              ? <i className="fa fa-spinner fa-spin" />
              : <i className="m-icon-swapright m-icon-white" />
            }
          </button>
        </div>{" "}
      </form>
    </React.Fragment>
  );

  const selectClientForm = (
    <React.Fragment>
      <form name="login" key="login">
        {clientOptions.length > 1 ? (
          <div className={clientClass}>
            {/*ie8, ie9 does not support html5 placeholder, so we just show field title for that*/}
            <label
              htmlFor="selectClient"
              className="control-label visible-ie8 visible-ie9"
            >
              Organization
            </label>
            <div className="input-icon">
              <i className="fa fa-building" />
              <Select
                inputId="selectClient"
                styles={clientSelectStyles}
                options={clientOptions}
                onChange={selectClient}
              />
            </div>{" "}
          </div>
        ) : null}
        <div className="form-actions">
          <button
            type="button"
            className="btn grey pull-left"
            onClick={() => backFromSelectClientForm()}
            aria-label="back"
          >
            Back <i className="m-icon-swapleft m-icon-white" />
          </button>
          <button
            type="button"
            className="btn green pull-right"
            aria-label="login"
            disabled={loading}
            onClick={() => login()}
          >
            Continue
            { loading
              ? <i className="fa fa-spinner fa-spin" />
              : <i className="m-icon-swapright m-icon-white" />
            }
          </button>
        </div>{" "}
      </form>
    </React.Fragment>
  );

  let confirmEmailClass = "form-group";
  if (confirmEmailValidationMessage) {
    confirmEmailClass += " has-error";
  }
  const emailForm = (
    <React.Fragment>
      <form name="email" key="email">
        <div className={emailClass}>
          {/*ie8, ie9 does not support html5 placeholder, so we just show field title for that*/}
          <label className="control-label visible-ie8 visible-ie9">Email</label>
          <div className="input-icon">
            <i className="fa fa-envelope" />
            <input
              id="email"
              className="form-control placeholder-no-fix"
              type="text"
              placeholder="Email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {emailValidationMessage ? (
            <span htmlFor="email" className="help-block">
              {emailValidationMessage}
            </span>
          ) : null}
        </div>
        <div className={confirmEmailClass}>
          <label className="control-label visible-ie8 visible-ie9">
            Confirm Email
          </label>
          <div className="input-icon">
            <i className="fa fa-envelope" />
            <input
              id="confirmEmail"
              className="form-control placeholder-no-fix"
              type="text"
              placeholder="Confirm Email"
              name="confirmemail"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
            />
          </div>
          {confirmEmailValidationMessage ? (
            <span htmlFor="password" className="help-block">
              {confirmEmailValidationMessage}
            </span>
          ) : null}
        </div>
        <div className="form-actions">
          { backButton }
          <button
            type="button"
            className="btn green pull-right"
            onClick={onClickSubmit}
            disabled={loading}
            aria-label="submit"
          >
            Submit
            { loading
              ? <i className="fa fa-spinner fa-spin" />
              : <i className="m-icon-swapright m-icon-white" />
            }
          </button>
        </div>{" "}
      </form>
    </React.Fragment>
  );

  const form =
    step === "start" || step == "reset-email-sent" || step === "vanco" || step === "existing-vanco"
      ? initialForm
      : step === "login"
      ? loginForm
      : step === "email"
      ? emailForm
      : step === "select-client"
      ? selectClientForm
      : null;
  const validationSummaryClass = validationMessage
    ? "validation-summary-errors"
    : "validation-summary-valid";

  return (
    <React.Fragment>
      <h4 className="form-title">{title}</h4>
      <div className={errorMessageClass}>
        <button className="close" onClick={() => setErrorMessage()} />
        <span>{errorMessage}</span>
      </div>
      <div className={validationSummaryClass}>
        <ul>{validationMessage ? <li>{validationMessage}</li> : null}</ul>
      </div>{" "}
      {form}
    </React.Fragment>
  );
};

export default LoginForm;
